import type { SkillTemplate } from './issue-log-xref.js';

export const agentTasksTemplate: SkillTemplate = {
  name: 'agent-tasks',
  description: 'Manage trigger-based agent tasks for deferred work items',
  version: '1.0.0',
  render: () => `---
name: agent-tasks
version: 1.0.0
description: Manage trigger-based agent tasks via the Canary CLI. Use when the user asks to create a task, schedule post-deploy work, set up reminders for flag cleanup, track knob deprecation, or manage deferred work items triggered by system events.
allowed-tools: Bash(canary task:*), Read, Grep, Glob
---

# Agent Tasks with Canary

Use \`canary task\` to create and manage **trigger-based work items** — deferred tasks that become actionable when a system event fires (deploy, flag change, schedule, etc.).

## Prerequisites

- \`canary\` CLI installed and authenticated (\`canary login\`)
- Your account must be a **superadmin** — agent tasks are a superadmin-only capability
- Run \`canary task --help\` for the full command reference

## Quick Reference

\`\`\`bash
# Create a task
canary task create --title "Remove feature flag" \\
  --description "Delete COOL_FEATURE flag and all gating code" \\
  --trigger flag-deleted --flag COOL_FEATURE

# List tasks
canary task list                                    # all tasks
canary task list --status waiting                   # waiting for trigger
canary task list --status actionable                # ready to work on
canary task list --trigger production-deploy        # by trigger type

# Manage tasks
canary task get <id>                                # view details + event history
canary task update <id> --title "New title"         # update task fields
canary task claim <id>                              # mark in progress
canary task complete <id>                           # mark done
canary task complete <id> --commit abc123           # done, link to resolution commit
canary task cancel <id>                             # cancel
canary task trigger <id>                            # manually fire trigger
\`\`\`

## Trigger Types

| Type | Fires when... | Auto-fired? | Required flag |
|------|---------------|-------------|---------------|
| \`production-deploy\` | Code deployed to production | **Yes** | — |
| \`flag-deprecated\` | Feature flag set to deprecated | Yes | \`--flag <name>\` |
| \`flag-cleanup\` | Feature flag set to ready_for_cleanup | Yes | \`--flag <name>\` |
| \`flag-deleted\` | Feature flag deleted | Yes | \`--flag <name>\` |
| \`knob-deprecated\` | Knob set to deprecated | Yes | \`--knob <key>\` |
| \`knob-cleanup\` | Knob set to ready_for_cleanup | Yes | \`--knob <key>\` |
| \`knob-deleted\` | Knob deleted | Yes | \`--knob <key>\` |
| \`scheduled\` | A date/time is reached | Yes | \`--scheduled-date <ISO>\` |
| \`pr-merged\` | A specific PR is merged | Yes | \`--pr <number>\` |
| \`immediate\` | Immediately (no waiting) | N/A | — |
| \`manual\` | Manually triggered via CLI | N/A | — |

**Note:** \`pr-merged\` tasks are scoped to the linked repository selected by \`--repo\`, \`--role\`, or \`--repo-id\`.

## Task Lifecycle

\`\`\`
                   ┌─── immediate ───► actionable
                   │                       │
  create ──────────┤                     claim
                   │                       │
                   └─── other types ──► waiting ──trigger──► actionable ──► in_progress ──► completed
                                                                                    ├──► canceled
                                                                                    └──► expired
\`\`\`

- **waiting**: Created, waiting for its trigger event
- **actionable**: Trigger fired (or immediate type), ready to be worked on
- **in_progress**: Claimed by someone, actively being worked
- **completed**: Done (optionally linked to a resolution commit)
- **canceled** / **expired**: Terminated without completion

## Common Workflows

### Post-deploy cleanup (remove temporary code after next production deploy)

\`\`\`bash
canary task create \\
  --title "Remove backwards-compat shim for widget API" \\
  --description "Old endpoint can be removed now that new endpoint is deployed." \\
  --trigger production-deploy \\
  --priority 1 \\
  --tag cleanup --tag api
\`\`\`

This task automatically becomes actionable after the next successful production deployment.

### Flag removal after GA

When a feature graduates from behind a flag:

\`\`\`bash
canary task create \\
  --title "Clean up COOL_FEATURE flag gating code" \\
  --description "Feature is GA. Remove all gating checks and delete the constant." \\
  --trigger flag-deprecated --flag COOL_FEATURE \\
  --tag cleanup --tag feature-flags
\`\`\`

Auto-fires when someone runs \`canary feature-flag lifecycle COOL_FEATURE --stage deprecated\`.

### Knob deprecation follow-up

\`\`\`bash
canary task create \\
  --title "Remove agent.truncation.enabled knob reads" \\
  --description "Replace all knob reads with the hardcoded final value." \\
  --trigger knob-deprecated --knob agent.truncation.enabled \\
  --tag cleanup
\`\`\`

### Scheduled future work

\`\`\`bash
canary task create \\
  --title "Rotate API signing keys" \\
  --description "Quarterly key rotation per security policy." \\
  --trigger scheduled --scheduled-date 2026-06-30T00:00:00Z \\
  --priority 2 --tag security
\`\`\`

### Immediate task (starts actionable, no trigger wait)

\`\`\`bash
canary task create \\
  --title "Fix type error in auth middleware" \\
  --description "Strict mode caught a missing null check. Needs immediate fix." \\
  --trigger immediate \\
  --priority 2 --assign dev@example.com
\`\`\`

## Integration Points

### Feature Flags → Tasks

Flag lifecycle changes automatically fire matching task triggers:
- \`canary feature-flag lifecycle <name> --stage deprecated\` → fires \`flag-deprecated\` tasks
- \`canary feature-flag lifecycle <name> --stage ready_for_cleanup\` → fires \`flag-cleanup\` tasks
- \`canary feature-flag delete <name>\` → fires \`flag-deleted\` tasks

### Knobs → Tasks

Same pattern as flags:
- \`canary knobs lifecycle <key> --stage deprecated\` → fires \`knob-deprecated\` tasks
- \`canary knobs lifecycle <key> --stage ready_for_cleanup\` → fires \`knob-cleanup\` tasks
- \`canary knobs delete <key>\` → fires \`knob-deleted\` tasks

### Production Deploys → Tasks

The deployment notifier Lambda fires \`production-deploy\` task triggers automatically on successful production deployments. Any waiting tasks with this trigger type transition to \`actionable\`.

## Tips

- \`--commit\` defaults to \`git HEAD\` — auto-detects the current commit
- \`--repo\` is auto-selected if the org has a single linked repository
- \`--tag\` is repeatable: \`--tag cleanup --tag api --tag urgent\`
- \`--description-file <path>\` reads description from a file (useful for long markdown descriptions)
- \`--json\` on any command returns structured JSON output for scripting
- \`--priority\`: 0 = normal (default), 1 = high, 2 = critical
- \`--expires <ISO date>\` sets an expiry after which the task auto-expires if not completed
- Use \`canary task trigger <id>\` to manually fire any waiting task's trigger
`,
};
